// Buttons. `primary` is the one gold action per panel; `danger` for destructive confirms; `ghost`
// for chrome. `loading` swaps the label for a spinner and marks the button busy (the width is kept).
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'secondary' | 'primary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Shorthand for variant="primary" (kept for the existing screens). */
  primary?: boolean;
  /** Shorthand for size="sm". */
  small?: boolean;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
  /** Leading glyph or sprite. */
  icon?: ReactNode;
}

export function Button({ variant, primary, small, size, loading, block, icon, className = '', children, disabled, type = 'button', ...rest }: ButtonProps) {
  const v = variant ?? (primary ? 'primary' : 'secondary');
  const s = size ?? (small ? 'sm' : 'md');
  const cls = ['fk-btn', v !== 'secondary' ? `fk-btn--${v}` : '', s !== 'md' ? `fk-btn--${s}` : '', block ? 'fk-btn--block' : '', className].filter(Boolean).join(' ');
  return (
    <button type={type} className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {icon && <span className="fk-btn__icon" aria-hidden="true">{icon}</span>}
      {children}
      {loading && <span className="fk-btn__spinner"><span className="fk-spinner" aria-hidden="true" /></span>}
    </button>
  );
}

/** Square button for a single glyph; `label` is required for the accessible name. */
export function IconButton({ label, className = '', children, ...rest }: ButtonProps & { label: string }) {
  return <Button className={`fk-btn--icon ${className}`} aria-label={label} title={rest.title ?? label} {...rest}>{children}</Button>;
}

export function ButtonRow({ children, className = '', end, ...rest }: { children: ReactNode; className?: string; end?: boolean } & HTMLAttributes<HTMLDivElement>) {
  return <div className={`fk-btn-row ${end ? 'fk-row--end' : ''} ${className}`} {...rest}>{children}</div>;
}
